import type React from 'react';
import type { BulkGroupAction } from '@tool-no-hu/shared/types';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Play, Square, X } from 'lucide-react';

interface BulkActionBarProps {
  /** Số nhóm đã chọn. Bằng 0 thì thanh ẩn. */
  count: number;
  /** Số nhóm đủ điều kiện cho từng hành động — hiện trên badge nút. */
  startCount: number;
  stopCount: number;
  /** Đang gọi API — khoá nút chống bấm hai lần. */
  busy: boolean;
  onAction: (action: BulkGroupAction) => void;
  onClear: () => void;
}

/**
 * Thanh thao tác hàng loạt nổi ở đáy Sảnh khi đang chọn nhóm.
 *
 * Chỉ Start/Stop — login nặng nên để thủ công từng nhóm. Badge trên mỗi nút là
 * số nhóm ĐỦ điều kiện trong lựa chọn hiện tại; bấm vào mở bảng xác nhận (nhóm
 * không đủ điều kiện sẽ bị bỏ qua, không phải lỗi).
 */
export function BulkActionBar({
  count,
  startCount,
  stopCount,
  busy,
  onAction,
  onClear,
}: BulkActionBarProps) {
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          className="fixed bottom-5 left-1/2 z-40 w-[min(880px,calc(100vw-2rem))] -translate-x-1/2"
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        >
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#252a38] bg-[#161920]/95 p-3 shadow-2xl backdrop-blur">
            <div className="flex items-baseline gap-1.5 whitespace-nowrap pl-1 pr-1">
              <span className="font-mono text-xl font-bold tabular-nums text-[#3b82f6]">
                {count}
              </span>
              <span className="text-xs text-[#64748b]">nhóm đã chọn</span>
            </div>

            <div className="h-8 w-px bg-[#252a38]" />

            <div className="flex flex-1 flex-wrap gap-2">
              <BulkButton
                label="Start"
                icon={<Play className="h-4 w-4" />}
                eligible={startCount}
                tone="success"
                busy={busy}
                onClick={() => {
                  onAction('start');
                }}
              />
              <BulkButton
                label="Stop"
                icon={<Square className="h-4 w-4" />}
                eligible={stopCount}
                tone="danger"
                busy={busy}
                onClick={() => {
                  onAction('stop');
                }}
              />
            </div>

            <button
              type="button"
              onClick={onClear}
              disabled={busy}
              aria-label="Bỏ chọn"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface BulkButtonProps {
  label: string;
  icon: React.ReactNode;
  eligible: number;
  tone: 'success' | 'danger';
  busy: boolean;
  onClick: () => void;
}

const TONE: Record<BulkButtonProps['tone'], string> = {
  success: 'text-[#4ade80] border-[#22c55e]/40 bg-[#22c55e]/10 hover:bg-[#22c55e]/20',
  danger: 'text-[#ef4444] border-[#ef4444]/40 bg-[#ef4444]/10 hover:bg-[#ef4444]/20',
};

function BulkButton({ label, icon, eligible, tone, busy, onClick }: BulkButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`flex h-10 items-center gap-2 rounded-xl border px-4 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${TONE[tone]}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
      <span className="rounded-md bg-black/20 px-1.5 py-0.5 font-mono text-[11px] font-bold tabular-nums">
        {eligible}
      </span>
    </button>
  );
}
